/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApiService } from 'app/api';
import { IApplicationContext } from 'app/context/types';
import { getObjectValueIfExists } from 'modules/utilities/Helpers';
import { SearchShopModulesFieldsForm } from '../components/ShopModule/ShopModuleSearchBar';
import { ICreateShopModule, IShopModule } from '../interfaces/shop-module';
import { ICreateTag, ITag } from '../interfaces/tag';

export type IShopModuleGetManySearchQuery = SearchShopModulesFieldsForm & {
	page: number;
};

export class ShopService {
	private apiService: ApiService;

	constructor(private applicationContext: IApplicationContext) {
		this.apiService = new ApiService(this.applicationContext);
	}

	getShopModules(searchQuery: IShopModuleGetManySearchQuery) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(this.applicationContext.getApiConfig().SHOP.GET_MODULES, null, {
					pathParams: this.formGetShopModulesSearchQuery(searchQuery),
				})
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	searchShopModules(searchKey: string) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().SHOP.SEARCH_MODULES,
					null,
					{
						pathParams: { searchKey },
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	deleteShopModule(id: number) {
		return this.apiService.invoke(
			this.applicationContext.getApiConfig().SHOP.DELETE_MODULE,
			null,
			{ reqPathParam: id },
		);
	}

	getShopModule(id: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().SHOP.GET_MODULE,
					{},
					{
						reqPathParam: id,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createShopModule(newShopModule: IShopModule) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().SHOP.CREATE_MODULE,
					this.formatShopModuleData(newShopModule),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	updateShopModule(updatedShopModule: IShopModule) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().SHOP.UPDATE_MODULE,
					this.formatShopModuleData(updatedShopModule),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	getNewShopModuleOrder() {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().SHOP.GET_NEW_SHOP_MODULE_ORDER,
					{},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	searchTags(searchKey: string) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(this.applicationContext.getApiConfig().TAG.SEARCH_TAGS, null, {
					pathParams: { searchKey },
				})
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	createTag(tag: ITag) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().TAG.CREATE_TAG,
					this.formatTagData(tag),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	updateTag(tag: ITag) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().TAG.UPDATE_TAG,
					this.formatTagData(tag),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	deleteTag(id: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(this.applicationContext.getApiConfig().TAG.DELETE_TAG, null, {
					reqPathParam: id,
				})
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	private formatShopModuleData(newShopModule: IShopModule): ICreateShopModule {
		return {
			...getObjectValueIfExists(newShopModule, 'id'),
			order: newShopModule.order,
			title: newShopModule.title,
			description: newShopModule.description,
			gemsCost: newShopModule.gemsCost,
			showAlways: newShopModule.showAlways,
			...getObjectValueIfExists(newShopModule, 'showFrom', ''),
			...getObjectValueIfExists(newShopModule, 'showTo', ''),
			...getObjectValueIfExists(newShopModule, 'showAfterCompleteRegion', -1),
			mainImageId: newShopModule.mainImage.id,
			backgroundImageId: newShopModule.backgroundImage.id,
			roadMapBackgroundImageId: newShopModule.roadMapBackgroundImage.id,
			items: newShopModule.items.map(item => ({
				...getObjectValueIfExists(item, 'id'),
				lessonId: item.lesson?.id,
			})),
			isNew: newShopModule.isNew,
			showRemainingTime: newShopModule.showRemainingTime,
			discountPercentage: newShopModule.discountPercentage,
			...getObjectValueIfExists(newShopModule, 'discountFrom', ''),
			...getObjectValueIfExists(newShopModule, 'discountTo', ''),
			customTagId: newShopModule.customTag?.id,
		};
	}

	private formatTagData({ id, title, image }: ITag): ICreateTag {
		return {
			id,
			title,
			imageId: image.id,
		};
	}

	private formGetShopModulesSearchQuery(
		searchQuery: IShopModuleGetManySearchQuery,
	) {
		const formattedSearchQuery: any = { ...searchQuery };
		if (searchQuery.region !== -1) {
			formattedSearchQuery.region = searchQuery.region;
		} else {
			delete formattedSearchQuery.region;
		}

		return formattedSearchQuery;
	}
}
